import {
  createPersonObject,
  createUserObject,
} from "@/features/app-user/adapters/appUser";
import { determineActionByRule } from "@/features/app-user/policies/actionRule";
import { determineRuleMatching } from "@/features/app-user/policies/itemRule";
import type {
  AppUserTableRow,
  MatchedUserRow,
  NoUserRow,
  NoWorkerUserRow,
  ViolatedRoleUserRow,
  ViolatedUserRow,
} from "@/features/app-user/types";
import { RULE_MATCHING } from "@/features/app-user/types";
import { P, match } from "ts-pattern";
import type { GetAppIntegrationUsersRes } from "./type";

export type GetAppIntegrationUsersResItem =
  GetAppIntegrationUsersRes["items"][number];

export const convertToUsersTableRow = (
  data: GetAppIntegrationUsersRes,
): AppUserTableRow[] =>
  data.items
    .map(convertToAppUserTableRow)
    .filter((row): row is AppUserTableRow => row !== undefined);

/**
 * AppUserドメインのデータ変換アダプター
 *
 * アプリケーション層とドメイン層の境界に位置し、APIレスポンスをドメインモデルに変換します。
 * このアダプターはドメインの純粋性を保護する役割を持ちます。
 *
 * 主な責務：
 * 1. APIレスポンスをドメインモデル（AppUserTableRow）に変換
 * 2. ドメインルール（RULE_MATCHING）に基づいたデータの検証
 * 3. ドメイン固有の値オブジェクト（nameInitials等）の生成
 * 4. 外部データの正規化（null/undefinedの処理）
 *
 * 設計意図：
 * - ドメインモデルをインフラストラクチャ（API）の詳細から保護
 * - ドメインロジックの再利用性を確保
 * - 外部システムの変更影響を局所化
 */
export const convertToAppUserTableRow = (
  item: GetAppIntegrationUsersResItem,
): AppUserTableRow | undefined => {
  const baseCommonFields = {
    displayName: item.user?.displayName ?? "",
    hasConfiguredMfa: item.user?.configuredMfa ?? false,
    isActive: item.user?.isActive ?? false,
    created_at: item.user?.createdAt ? new Date(item.user?.createdAt) : null,
    deactivated_at: item.user?.deactivatedAt
      ? new Date(item.user?.deactivatedAt)
      : null,
    lastSignedInAt: item.user?.lastSignedInAt
      ? new Date(item.user?.lastSignedInAt)
      : null,
    statusName: item.user?.statusName ?? "",
    roleName: item.user?.actualRoleName ?? "",
    assignedRoleName:
      item.worker?.assignedRoleName ?? item.worker?.assignedRoleId ?? "",
    canChangePerson: item.canChangePerson ?? false,
    canDelete: item.canDelete ?? false,
  };

  const person = createPersonObject(item.worker ?? undefined);
  const user = createUserObject(item.user ?? undefined);

  const ruleMatchItem = {
    ruleMatching: item.ruleMatching,
    user,
    worker: person,
  };

  const matchedRule = determineRuleMatching(ruleMatchItem);

  return match({ ruleMatching: matchedRule, person, user })
    .with(
      {
        ruleMatching: RULE_MATCHING.MATCHED,
        person: P.not(undefined),
        user: P.not(undefined),
      },
      () => {
        if (!user || !person) {
          throw new Error("User and person must be defined for matched rule");
        }
        const matchedRow: MatchedUserRow = {
          ...baseCommonFields,
          user,
          person,
          rule: RULE_MATCHING.MATCHED,
          action: determineActionByRule(item.action, RULE_MATCHING.MATCHED),
        };
        return matchedRow;
      },
    )
    .with(
      {
        ruleMatching: RULE_MATCHING.VIOLATED,
        person: P.not(undefined),
        user: P.not(undefined),
      },
      () => {
        if (!user || !person) {
          throw new Error("User and person must be defined for violated rule");
        }
        const violatedRow: ViolatedUserRow = {
          ...baseCommonFields,
          user,
          person,
          rule: RULE_MATCHING.VIOLATED,
          action: determineActionByRule(item.action, RULE_MATCHING.VIOLATED),
        };
        return violatedRow;
      },
    )
    .with(
      {
        ruleMatching: RULE_MATCHING.VIOLATED_ROLE,
        person: P.not(undefined),
        user: P.not(undefined),
      },
      () => {
        if (!user || !person) {
          throw new Error(
            "User and person must be defined for violated role rule",
          );
        }
        const violatedRoleRow: ViolatedRoleUserRow = {
          ...baseCommonFields,
          user,
          person,
          rule: RULE_MATCHING.VIOLATED_ROLE,
        };
        return violatedRoleRow;
      },
    )
    .with(
      { ruleMatching: RULE_MATCHING.NO_WORKER, user: P.not(undefined) },
      () => {
        if (!user) {
          throw new Error("User must be defined for no worker rule");
        }
        const noWorkerRow: NoWorkerUserRow = {
          ...baseCommonFields,
          user,
          person: undefined,
          rule: RULE_MATCHING.NO_WORKER,
          action: determineActionByRule(item.action, RULE_MATCHING.NO_WORKER),
        };
        return noWorkerRow;
      },
    )
    .with(
      { ruleMatching: RULE_MATCHING.NO_USER, person: P.not(undefined) },
      () => {
        if (!person) {
          throw new Error("Person must be defined for no user rule");
        }
        const noUserRow: NoUserRow = {
          person,
          user: undefined,
          rule: RULE_MATCHING.NO_USER,
          assignedRoleName:
            item.worker?.assignedRoleName ?? item.worker?.assignedRoleId ?? "",
          action: determineActionByRule(item.action, RULE_MATCHING.NO_USER),
        };
        return noUserRow;
      },
    )
    .otherwise(() => undefined);
};
